import { LitElement, html, type PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import style from './style/zaptic-dynamic-condition-style'
import inputStyle from './style/zaptic-input-style'
import { styleMap } from 'lit/directives/style-map.js'

import type { ReservedOperations } from 'json-logic-js'

export enum Events {
    onUpdated = 'on-updated',
    onDeleted = 'on-deleted',
}

export interface CustomEventPayload {
    [Events.onUpdated]: SingleCondition | null
    [Events.onDeleted]: void
}

const CONDITIONS: Array<{
    resourceKey: string
    value: AllowedOperator
}> = [
    // { label: window.i18n('shared:json-logic.operations.equal-to'), value: '==' },
    { resourceKey: 'shared:json-logic.operations.equal-to', value: '==' },
    { resourceKey: 'shared:json-logic.operations.not-equal-to', value: '!=' },
    { resourceKey: 'shared:json-logic.operations.less-than', value: '<' },
    { resourceKey: 'shared:json-logic.operations.less-than-or-equal-to', value: '<=' },
    { resourceKey: 'shared:json-logic.operations.greater-than', value: '>' },
    { resourceKey: 'shared:json-logic.operations.greater-than-or-equal-to', value: '>=' },
]

type AllowedOperator = Extract<ReservedOperations, '==' | '!=' | '<' | '>' | '<=' | '>='>
type Value = [
    {
        var: string
    },
    string,
]
// how to tie this to AllwedOperator?
export type SingleCondition =
    | { '==': Value }
    | { '!=': Value }
    | { '<': Value }
    | { '>': Value }
    | { '<=': Value }
    | { '>=': Value }

@customElement('zaptic-dynamic-condition')
export class ZapticDynamicCondition extends LitElement {
    static styles = [style, inputStyle]

    @property({ type: Array })
    variableNames: string[]

    @property({ type: Array })
    conditions: Array<{ label: string; value: string }>

    @property()
    variablePlaceholder: string

    @property()
    conditionPlaceholder: string

    @property()
    valuePlaceholder: string

    @property({ type: Boolean })
    deletable: boolean

    @property({
        type: Object,
        hasChanged: (newVal, oldVal) => JSON.stringify(newVal) !== JSON.stringify(oldVal),
    })
    value: SingleCondition

    @property({ type: Boolean })
    readonly: boolean

    @state()
    _selectedVariable: string

    @state()
    _selectedOperator: AllowedOperator

    @state()
    _valueToCompare: string = ''

    @state()
    _initialized = false

    firstUpdated() {
        this._initialized = true
    }

    private init() {
        if (this._initialized) return
        this.getValuesFromProp()
    }

    private getValuesFromProp() {
        if (!this.value) return
        const [operator] = Object.keys(this.value)
        this._selectedOperator = operator as AllowedOperator
        const [{ var: variable }, compareValue] = Object.values(this.value)[0]
        this._valueToCompare = compareValue
        this._selectedVariable = variable
    }

    willUpdate(changedProperties: PropertyValues<ZapticDynamicCondition>) {
        this.init()
        if (
            changedProperties.has('_selectedOperator') ||
            changedProperties.has('_selectedVariable') ||
            changedProperties.has('_valueToCompare')
        ) {
            const payload = this.isConditionValid()
                ? { [this._selectedOperator]: [{ var: this._selectedVariable }, this._valueToCompare] }
                : null
            this.dispatchEvent(
                new CustomEvent<CustomEventPayload['on-updated']>(Events.onUpdated, {
                    detail: payload as SingleCondition,
                })
            )
        }
        if (changedProperties.has('value')) {
            this.getValuesFromProp()
        }
    }

    private emitDelete() {
        this.dispatchEvent(new CustomEvent<CustomEventPayload['on-deleted']>(Events.onDeleted))
    }

    private isConditionValid() {
        return !!this._selectedVariable && !!this._selectedOperator && !!this._valueToCompare
    }

    render() {
        return html`
            <div class="container">
                <zaptic-select
                    placeholder=${this.variablePlaceholder}
                    selected=${this._selectedVariable}
                    ?isInvalid=${!this._selectedVariable}
                    .options=${this.variableNames.map((value) => ({ value, label: value }))}
                    .readonly=${this.readonly}
                    @on-selected=${(val: CustomEvent<{ selected: any }>) => {
                        this._selectedVariable = val.detail.selected
                    }}
                ></zaptic-select>
                <zaptic-select
                    placeholder=${this.conditionPlaceholder}
                    selected=${this._selectedOperator}
                    ?isInvalid=${!this._selectedOperator}
                    .readonly=${this.readonly}
                    .options=${CONDITIONS.map(({ resourceKey, value }) => ({ label: window.i18n(resourceKey), value }))}
                    @on-selected=${(val: CustomEvent<{ selected: any }>) => {
                        this._selectedOperator = val.detail.selected
                    }}
                ></zaptic-select>
                <input
                    class="input"
                    placeholder=${this.valuePlaceholder}
                    required
                    type="text"
                    ?readonly=${this.readonly}
                    ?invalid=${!this._valueToCompare}
                    .value=${this._valueToCompare}
                    @input=${(val: { target: { value: string } }) => {
                        this._valueToCompare = val.target.value.trim()
                    }}
                />
                ${!this.readonly
                    ? html`<zaptic-button
                          style=${styleMap({
                              visibility: this.deletable ? '' : 'hidden',
                          })}
                          type="text"
                          @click=${() => this.emitDelete()}
                          ><zaptic-icon icon-name="delete-cross-in-circle"></zaptic-icon
                      ></zaptic-button>`
                    : null}
            </div>
        `
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'zaptic-dynamic-condition': ZapticDynamicCondition
    }
}
